import { Plugin } from './Plugin';
import { EntityPlugin } from './EntityPlugin';
import { ScenePlugin } from './ScenePlugin';
import { MovePlugin } from './MovePlugin';
import { RotatePlugin } from './RotatePlugin';
import { customization } from 'storage';
import { Vector3, Vector2, Object3D } from 'three';
import { SphereGeometry, MeshBasicMaterial, Mesh, Frustum, Matrix4, Raycaster } from 'three';

// import { Stats } from 'libs';

const TEMP_V3 = new Vector3();

export class PinsPlugin extends Plugin{
	constructor(manager){
		super(manager);

		this.dependencies.add(EntityPlugin);
		this.dependencies.add(ScenePlugin);
		this.dependencies.add(MovePlugin);
		this.dependencies.add(MovePlugin);

		this.pins 		= new Map();
		this.frustum 	= new Frustum();
		this.mat4 		= new Matrix4();


		this.raycaster = new Raycaster();

		this.collisionEnabled = true;
		this.shouldTestCollisions = false;

		window["pinsPlugin"] = this;
	}

	attach(){
		this.entityPlugin.on('entity-added', this.onEntityAdded);
		this.entityPlugin.on('entity-updated', this.onEntityUpdated);
		this.entityPlugin.on('entity-removed', this.onEntityRemoved);
		this.scenePlugin.on('scene-updated', this.onSceneChanged);
		this.scenePlugin.on('scene-changed', this.onSceneChanged);

		customization.on('customization-loaded', this.onCustomizationLoaded);
		customization.on('product-modified', this.onProductChanged);
		this.viewer.rendering.ticker.on('tick', this.tick);
	}

	detach(){
		this.entityPlugin.off('entity-added', this.onEntityAdded);
		this.entityPlugin.off('entity-updated', this.onEntityUpdated);
		this.entityPlugin.off('entity-removed', this.onEntityRemoved);
		this.scenePlugin.off('scene-updated', this.onSceneChanged);
		this.scenePlugin.off('scene-changed', this.onSceneChanged);

		customization.off('customization-loaded', this.onCustomizationLoaded);
		customization.off('product-modified', this.onProductChanged);
		this.viewer.rendering.ticker.off('tick', this.tick);
	}

	get entityPlugin(){
		return this.manager.get(EntityPlugin);
	}

	get scenePlugin(){
		return this.manager.get(ScenePlugin);
	}

	get rotatePlugin(){
		return this.manager.get(RotatePlugin);
	}

	get movePlugin(){
		return this.manager.get(MovePlugin);
	}

	get camera(){
		return this.viewer.rendering.camera;
	}

	clearPins(){
		this.pins.forEach(pin => pin.parent ? pin.parent.remove(pin) : '');
		this.pins.clear();
	}

	remove(key){
		if(this.pins.has(key)){
			const pin = this.pins.get(key);
			pin.parent ? pin.parent.remove(pin) : '';
			this.pins.delete(key);
		}
	}

	add(key, pin){
		this.remove(key);
		this.pins.set(key, pin);
		this.viewer.add(pin);
	}

	onEntityAdded = (entity) => {
		this.addDependenciesPins(entity);
		this.emit('pins-changed');
	}

	onEntityRemoved = (entity) => {
		this.removeDependenciesPins(entity);
		this.emit('pins-changed');
	}

	onEntityUpdated = (entity) => {
		this.removeDependenciesPins(entity);
		this.addDependenciesPins(entity);
		this.emit('pins-changed');
	}

	onSceneChanged = () => {
		this.shouldTestCollisions = true;
	}

	onProductChanged = (key) => {
		this.updateGhosts();
	}

	removeDependenciesPins(entity){
		entity.product.entity.dependencies_types.forEach(type => {
			this.remove(type.code);
		})
	}

	addDependenciesPins(entity){
		const scene = customization.scene;
		entity.product.entity.dependencies_types.forEach(type => {
			const data = entity.product.entity.dependenciesTypesIds.find(d => d.entity_type_id == type.id);
			const data2 = scene.entityTypesIds.find(t => t.entity_type_id == type.id);

			const pin = new DependencyPin(type, data.pin, data2 ? data2.camera : undefined, entity.product.entity.code);
			entity.wrapper.add(pin)
			this.pins.set(type.code, pin);
		});
	}

	onCustomizationLoaded = () => {
		this.clearPins();
		const scene = customization.scene;
		const isBathtubOrShower = scene.bathtub_or_shower;

		const allowedEquipment = [...customization.equipment].map(eqt => storage.equipment.get(eqt).id)

		const showerOrBathtubIds = scene.equipment.filter(eq => (eq.slug == "baignoire" || eq.slug == "douche") && !allowedEquipment.includes(eq.id) ).map(eq => eq.id);

		scene.entity_types.forEach(type => {
			const data = scene.entityTypesIds.find(t => t.entity_type_id == type.id);
			const isGhost = !allowedEquipment.includes(type.equipment_id);

			if(data && !(isBathtubOrShower && showerOrBathtubIds.includes(type.equipment_id))){
				const isGhost = !allowedEquipment.includes(type.equipment_id);
				const pin = new Pin(type, data.pin, data.camera, isGhost);
				this.add(type.code, pin);
			}
		});

		this.entityPlugin.entities.forEach(entity => {
			this.removeDependenciesPins(entity);

			if(entity.product
			&& entity.product.entity
			&& entity.product.entity.entity_type
			&& allowedEquipment.includes(entity.product.entity.entity_type.equipment_id))
				this.addDependenciesPins(entity);
		})
		this.emit('pins-changed');
	}

	updateGhosts = () => {
		const allowedEquipment = [...customization.equipment].map(eqt => storage.equipment.get(eqt).id)
		let updated = false;
		this.pins.forEach(p => {
			const isGhost = !allowedEquipment.includes(p.entityType.equipment_id);
			if(p.isGhost != isGhost){
				p.isGhost = isGhost;
				updated = true;
			}
		});
		if(updated){
			this.emit('pins-changed');
		}
	}

	tick = () => {
		this.frustum.setFromProjectionMatrix(this.mat4.multiplyMatrices(
			this.camera.projectionMatrix,
			this.camera.matrixWorldInverse
		));

		const notmoving = (!this.rotatePlugin.rotating && !this.movePlugin.moving);

		if(this.notmoving != notmoving){
			this.notmoving = notmoving;
			if(notmoving)
				this.shouldTestCollisions = true;
		}

		this.pins.forEach(p => this.updateScreenCoords(p));

		if(this.shouldTestCollisions){
			this.shouldTestCollisions = false;
		}
	}

	updateScreenCoords(pin){
		pin.getWorldPosition(TEMP_V3);
		pin.visible = this.frustum.containsPoint(TEMP_V3);
		TEMP_V3.project(this.camera);

		pin.screen.set(TEMP_V3.x, -TEMP_V3.y);

		if(this.shouldTestCollisions && this.collisionEnabled && pin.visible && this.scenePlugin.scene){
			TEMP_V3.subVectors(pin.position, this.camera.position);
			this.raycaster.far = TEMP_V3.length();

			this.raycaster.set(this.camera.position, TEMP_V3.normalize());
			var intersects = this.raycaster.intersectObject(this.scenePlugin.scene, true);

			if(intersects.length && intersects[0].distance < this.raycaster.far){
				pin.collisionHidden = true;
			}
			else pin.collisionHidden = false;
		}

		if(!this.collisionEnabled){
			pin.collisionHidden = false;
		}
	}

	test(){
		this.frustum.setFromProjectionMatrix(this.mat4.multiplyMatrices(
			this.camera.projectionMatrix,
			this.camera.matrixWorldInverse
		));
		this.pins.forEach(pin => {
			pin.getWorldPosition(TEMP_V3);
			pin.visible = this.frustum.containsPoint(TEMP_V3);
			TEMP_V3.project(this.camera);

			pin.screen.set(TEMP_V3.x, -TEMP_V3.y);

			if(pin.visible && this.scenePlugin.scene){
				TEMP_V3.subVectors(pin.position, this.camera.position);
				this.raycaster.far = TEMP_V3.length();

				this.raycaster.set(this.camera.position, TEMP_V3.normalize());
				var intersects = this.raycaster.intersectObject(this.scenePlugin.scene, true);

				if(intersects.length){
					console.log('code', pin.code, 'distance', this.raycaster.far, intersects);
				}
			}
		});

	}
}

export class Pin extends Object3D{
	constructor(type, position = {x:0, y:0, z:0}, camera = {position: {x: 0, y: 0, z: 0}, rotation: {x: 0, y: 0, z: 0}}, isGhost=false){
		super();

		this.entityType = type;
		this.position.set(
			position.x,
			position.y,
			position.z,
		);

		this.collisionHidden = false;

		this.camera = camera;

		this.isGhost = isGhost;

		this.screen = new Vector2();

		this.debug();
	}

	get code(){
		return this.entityType.code;
	}

	debug(){
		this.debugItem = new Mesh(new SphereGeometry(0.02), new MeshBasicMaterial( 0x0000ff ) ) ;
		return this.debugItem;
	}
}

export class DependencyPin extends Pin{
	constructor(type, position = {x:0, y:0, z:0}, camera = {position: {x: 0, y: 0, z: 0}}, dependencyCode){
		super(type, position, camera);

		this.dependencyCode = dependencyCode;
	}

}